import { getWorkspaceAgentPanelZeroStateSuggestionById } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { QueryParamNames, type QueryType } from '~/constants/search';
import { setQueryParams } from '~/utils/PathUtils';

/** Stable marketing slugs for email deep links (WORK-664). */
export const WORKSPACE_AGENT_MARKETING_USE_CASE_SLUGS = {
  'get-summary': 'hero-summarize-status',
  'bulk-rename': 'hero-bulk-rename',
  'bulk-tag': 'hero-bulk-tag-metadata',
  'generate-variants': 'hero-artist-variants',
} as const;

export type WorkspaceAgentMarketingUseCaseSlug = keyof typeof WORKSPACE_AGENT_MARKETING_USE_CASE_SLUGS;

const isWorkspaceAgentMarketingUseCaseSlug = (value: string): value is WorkspaceAgentMarketingUseCaseSlug =>
  Object.hasOwn(WORKSPACE_AGENT_MARKETING_USE_CASE_SLUGS, value);

export type ParsedWorkspaceAgentMarketingUrlParams = {
  hasAgentUseCaseParam: boolean;
  shouldOpenPanel: boolean;
  suggestionId: string | null;
};

export const resolveWorkspaceAgentMarketingUseCaseToSuggestionId = (rawUseCase: string | null): string | null => {
  if (!rawUseCase) {
    return null;
  }

  const trimmed = rawUseCase.trim();

  if (!trimmed) {
    return null;
  }

  const normalizedSlug = trimmed.toLowerCase();

  if (isWorkspaceAgentMarketingUseCaseSlug(normalizedSlug)) {
    return WORKSPACE_AGENT_MARKETING_USE_CASE_SLUGS[normalizedSlug];
  }

  if (getWorkspaceAgentPanelZeroStateSuggestionById(trimmed)) {
    return trimmed;
  }

  return null;
};

export const parseWorkspaceAgentMarketingUrlParams = (search: string): ParsedWorkspaceAgentMarketingUrlParams => {
  const params = new URLSearchParams(search);
  const agentUseCaseRaw = params.get(QueryParamNames.agentUseCase);
  const hasAgentUseCaseParam = agentUseCaseRaw !== null;
  const suggestionId = resolveWorkspaceAgentMarketingUseCaseToSuggestionId(agentUseCaseRaw);

  return {
    hasAgentUseCaseParam,
    shouldOpenPanel: hasAgentUseCaseParam,
    suggestionId,
  };
};

export const WORKSPACE_AGENT_MARKETING_QUERY_PARAM_KEYS = [QueryParamNames.agentUseCase] as const;

export const stripWorkspaceAgentMarketingQueryParams = (method: 'push' | 'replace' = 'replace') => {
  const clearedParams = WORKSPACE_AGENT_MARKETING_QUERY_PARAM_KEYS.reduce<QueryType>((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});

  setQueryParams(clearedParams, method);
};
